import React, { useState, useEffect } from 'react';
import { Heart, BookOpen, Users, Calendar, ExternalLink, Eye } from 'lucide-react';
import { useAuth } from '../AuthContext';
import { ApiService } from '../services/api';
import { toast } from 'react-hot-toast';

interface ChannelHeaderProps {
    channelId: number;
    channelName: string;
    language?: string;
}

interface ChannelInfo {
    id: number;
    name: string;
    description?: string;
    subscriber_count?: number;
    total_videos?: number;
    total_views?: number;
    last_posted?: string;
    youtube_channel_id?: string;
    language?: string;
    is_favourited?: boolean;
}

export const ChannelHeader: React.FC<ChannelHeaderProps> = ({ channelId, channelName, language }) => {
    const { isAuthenticated } = useAuth();
    const [channelInfo, setChannelInfo] = useState<ChannelInfo | null>(null);
    const [loading, setLoading] = useState(true);
    const [favouriting, setFavouriting] = useState(false);

    useEffect(() => {
        const fetchChannelInfo = async () => {
            try {
                setLoading(true);
                // Fetch channel details including favorite status
                const response = await ApiService.getChannels({
                    page: 1,
                    limit: 1,
                    search: channelId.toString(), // Search by ID instead of name
                    userId: isAuthenticated ? 'current' : undefined
                });

                const channel = response.data.find(c => c.id === channelId);
                if (channel) {
                    setChannelInfo(channel);
                }
            } catch (error) {
                console.error('Error fetching channel info:', error);
                // Fallback to basic info
                setChannelInfo({
                    id: channelId,
                    name: channelName,
                    language: language
                });
            } finally {
                setLoading(false);
            }
        };

        fetchChannelInfo();
    }, [channelId, channelName, language, isAuthenticated]);

    const handleToggleFavourite = async () => {
        if (!isAuthenticated) {
            toast('Please log in to add channels to your favourites! ❤️', {
                icon: '❤️',
                style: {
                    borderRadius: '10px',
                    background: '#333',
                    color: '#fff',
                },
            });
            return;
        }

        if (!channelInfo) return;

        setFavouriting(true);
        try {
            if (channelInfo.is_favourited) {
                await ApiService.removeFromFavourites(channelId);
                setChannelInfo(prev => prev ? { ...prev, is_favourited: false } : null);
                toast.success(`Removed ${channelInfo.name} from favourites`);
            } else {
                await ApiService.addToFavourites(channelId);
                setChannelInfo(prev => prev ? { ...prev, is_favourited: true } : null);
                toast.success(`Added ${channelInfo.name} to favourites`);
            }
        } catch (error: any) {
            console.error('Error toggling favourite:', error);

            // Handle specific error cases
            if (error.message?.includes('not found in favourites') || error.message?.includes('404')) {
                // Channel wasn't actually in favourites, sync the state
                setChannelInfo(prev => prev ? { ...prev, is_favourited: false } : null);
                toast.success(`Channel was already removed from favourites`);
            } else if (error.message?.includes('already in favourites') || error.message?.includes('409')) {
                // Channel was already favourited, sync the state
                setChannelInfo(prev => prev ? { ...prev, is_favourited: true } : null);
                toast.success(`Channel was already in favourites`);
            } else {
                toast.error('Failed to update favourite status. Please try again.');
            }
        } finally {
            setFavouriting(false);
        }
    };

    const getLanguageName = (code: string | null | undefined): string => {
        if (!code) return 'N/A';
        const languageNames: { [key: string]: string } = {
            en: 'English',
            es: 'Español',
            ko: '한국어 (Korean)',
            ja: '日本語 (Japanese)',
            fr: 'Français',
            de: 'Deutsch',
            pt: 'Português',
            ru: 'Русский',
            zh: '中文 (Chinese)',
            id: 'Bahasa Indonesia',
            hi: 'हिन्दी (Hindi)',
            tl: 'Tagalog',
            hil: 'Hiligaynon',
            ar: 'العربية (Arabic)',
            it: 'Italiano',
            lus: 'Mizo',
            my: 'Burmese (မြန်မာဘာသာ)',
            th: 'Thai (ภาษาไทย)',
            uzn: 'Uzbek (Oʻzbekcha)',
            vi: 'Vietnamese (Tiếng Việt)',
        };
        return languageNames[code.toLowerCase()] || code;
    };

    const formatDate = (dateString: string | null | undefined): string => {
        if (!dateString) return 'Unknown';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatNumber = (num: number | null | undefined): string => {
        if (!num) return '0';
        if (num >= 1000000) {
            return (num / 1000000).toFixed(1) + 'M';
        } else if (num >= 1000) {
            return (num / 1000).toFixed(1) + 'K';
        }
        return num.toString();
    };

    if (loading) {
        return (
            <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 py-6">
                <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="animate-pulse">
                        <div className="h-8 bg-gray-300 dark:bg-gray-600 rounded w-1/3 mb-4"></div>
                        <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/2"></div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 py-6">
            <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    {/* Channel Info */}
                    <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="flex-shrink-0">
                                <img
                                    src={`/media/thumbnails/${channelId}.jpg`}
                                    alt={`${channelInfo?.name || channelName} thumbnail`}
                                    className="w-16 h-16 rounded-full object-cover border-2 border-gray-300 dark:border-gray-600"
                                    onError={(e) => {
                                        e.currentTarget.style.display = 'none';
                                        e.currentTarget.nextElementSibling?.classList.remove('hidden');
                                    }}
                                />
                                <div className="w-16 h-16 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center border-2 border-gray-300 dark:border-gray-600 hidden">
                                    <BookOpen className="w-8 h-8 text-gray-500 dark:text-gray-400" />
                                </div>
                            </div>

                            <div className="flex-1 min-w-0">
                                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white truncate">
                                    {channelInfo?.name || channelName}
                                </h1>
                                {channelInfo?.description && (
                                    <p className="text-gray-600 dark:text-gray-400 text-sm mt-1 line-clamp-2">
                                        {channelInfo.description}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Channel Stats */}
                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                            {channelInfo?.subscriber_count && (
                                <div className="flex items-center gap-1">
                                    <Users className="w-4 h-4" />
                                    <span>{formatNumber(channelInfo.subscriber_count)} subscribers</span>
                                </div>
                            )}

                            {channelInfo?.total_videos && (
                                <div className="flex items-center gap-1">
                                    <BookOpen className="w-4 h-4" />
                                    <span>{formatNumber(channelInfo.total_videos)} videos</span>
                                </div>
                            )}

                            {channelInfo?.total_views && (
                                <div className="flex items-center gap-1">
                                    <Eye className="w-4 h-4" />
                                    <span>{formatNumber(channelInfo.total_views)} views</span>
                                </div>
                            )}

                            {channelInfo?.last_posted && (
                                <div className="flex items-center gap-1">
                                    <Calendar className="w-4 h-4" />
                                    <span>Last posted: {formatDate(channelInfo.last_posted)}</span>
                                </div>
                            )}

                            {channelInfo?.language && (
                                <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded-full text-xs">
                                    {getLanguageName(channelInfo.language)}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-3">
                        {channelInfo?.youtube_channel_id && (
                            <a
                                href={`https://www.youtube.com/channel/${channelInfo.youtube_channel_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                            >
                                <ExternalLink className="w-4 h-4" />
                                <span className="hidden sm:inline">YouTube</span>
                            </a>
                        )}

                        <button
                            onClick={handleToggleFavourite}
                            disabled={favouriting}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${isAuthenticated && channelInfo?.is_favourited
                                ? 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-800'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                }`}
                            title={
                                isAuthenticated
                                    ? (channelInfo?.is_favourited ? `Remove ${channelInfo?.name || channelName} from favourites` : `Add ${channelInfo?.name || channelName} to favourites`)
                                    : 'Login to add to favourites! ❤️'
                            }
                        >
                            <Heart className={`w-4 h-4 ${isAuthenticated && channelInfo?.is_favourited ? 'fill-current' : ''}`} />
                            <span className="hidden sm:inline">
                                {isAuthenticated && channelInfo?.is_favourited ? 'Favourited' : 'Add to Favourites'}
                            </span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}; 